/**
 * Desktop Megamenu Web Component
 * Handles multi-level navigation with hover interactions and ARIA support
 * 
 * Todo: 
 * - Make it more optimized. 
 * - Remove methods if not used.
 * - Need to make it more modular and reusable.
 */

class DesktopMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.hoverTimeouts = new Map();
    this.hoverDelay = 100;
  }

  connectedCallback() {
    this.setupFirstLevel();
    this.setupSecondLevel();
    this.setupThirdLevel();
    this.setupFourthLevel();
    this.setupKeyboard();
    this.highlightCurrentPage();
  }

  disconnectedCallback() {
    this.hoverTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.hoverTimeouts.clear();
  }
  
  setupFirstLevel() {
    const menuItems = this.querySelectorAll('.list-menu__item');
    menuItems.forEach(item => {
      const link = item.querySelector('.header-nav__link--has-submenu');
      const megamenu = item.querySelector('.header-megamenu');

      if (!link || !megamenu) return;
      
      const menuId = `megamenu-${item.dataset.menuItem}`;
      megamenu.id = menuId;
      link.setAttribute('aria-controls', menuId);
      link.setAttribute('aria-expanded', 'false');
      megamenu.setAttribute('aria-hidden', 'true');
      
      let hoverTimeout = null;

      // Show megamenu
      const showMegamenu = () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
        }
        
        link.setAttribute('aria-expanded', 'true');
        megamenu.setAttribute('aria-hidden', 'false');
        item.classList.add('is-open');

        const firstSecondLevelItem = megamenu.querySelector('.megamenu-nav__item.second-level-item');
        if (firstSecondLevelItem) {
          megamenu.querySelectorAll('.megamenu-nav__item.is-active').forEach(activeItem => {
            activeItem.classList.remove('is-active');
          });

          const hasSubmenu = firstSecondLevelItem.querySelector('.megamenu-nav__link--has-submenu');
          if (hasSubmenu) {
            firstSecondLevelItem.classList.add('is-active');
            
            // Remove active state from all third-level items in this megamenu
            megamenu.querySelectorAll('.megamenu-submenu__link.is-active').forEach(activeLink => {
              activeLink.classList.remove('is-active');
            });
              
          }
        }
      };

      // Hide megamenu
      const hideMegamenu = () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
        }
        
        hoverTimeout = setTimeout(() => {
          link.setAttribute('aria-expanded', 'false');
          megamenu.setAttribute('aria-hidden', 'true');
          item.classList.remove('is-open');

          megamenu.querySelectorAll('.megamenu-nav__item.is-active').forEach(activeItem => {
            activeItem.classList.remove('is-active');
          });

          // NEW: Remove active state from third-level items
          megamenu.querySelectorAll('.megamenu-submenu__link.is-active').forEach(activeLink => {
            activeLink.classList.remove('is-active');
          });
          
          hoverTimeout = null;
        }, this.hoverDelay);
      };

      // Single event listener on the entire menu item container
      item.addEventListener('mouseenter', () => {
        showMegamenu();
      });
      item.addEventListener('mouseleave', (e) => {
        // Check if we're moving to the megamenu
        if (e.relatedTarget && megamenu.contains(e.relatedTarget)) {
          return;
        }
        hideMegamenu();
      });
    });
  }

  /**
   * Highlights the current page in the navigation
   * Matches the last segment of the URL (e.g., "camping-outdoor" from "/collections/camping-outdoor")
   */
  highlightCurrentPage() {
    const currentPath = window.location.pathname;
    const currentSegment = currentPath.split('/').filter(Boolean).pop();
    
    if (!currentSegment) return;

    const menuItems = this.querySelectorAll('.list-menu__item.first-level-item');

    menuItems.forEach(item => {
      const link = item.querySelector('.header-nav__link');
      if (!link) return;

      const menuUrl = link.getAttribute('data-menu-url');
      if (!menuUrl || menuUrl === '#') return;

      const menuSegment = menuUrl.split('/').filter(Boolean).pop();
      
      if (menuSegment === currentSegment) {
        link.classList.add('is-current-page');
      }
    });
  }
  
  setupSecondLevel() {
    const secondLevelItems = this.querySelectorAll('.megamenu-nav__item');

    secondLevelItems.forEach(item => {
      item.addEventListener('mouseenter', () => {
        const megamenu = item.closest('.header-megamenu');

        if (megamenu) {
          megamenu.querySelectorAll('.megamenu-nav__item.is-active').forEach(activeItem => {
            activeItem.classList.remove('is-active');
          });
        }

        const hasSubmenu = item.querySelector('.megamenu-nav__link--has-submenu');
        if (hasSubmenu) {
          item.classList.add('is-active');

          if (megamenu) {
            megamenu.querySelectorAll('.megamenu-submenu__link.is-active').forEach(activeLink => {
              activeLink.classList.remove('is-active');
            });
          }
        }
      });

      // Remove active state when leaving the entire second level item
      item.addEventListener('mouseleave', (e) => {
        
        const megamenu = item.closest('.header-megamenu');
        if (megamenu && !megamenu.contains(e.relatedTarget)) {
          item.classList.remove('is-active');
          
          // Also remove active state from third-level items
          megamenu.querySelectorAll('.megamenu-submenu__link.is-active').forEach(activeLink => {
            activeLink.classList.remove('is-active');
          });
        }
      });
    });
  }

  setupThirdLevel() {
    const thirdLevelItems = this.querySelectorAll('.megamenu-submenu__link');
  
    thirdLevelItems.forEach(link => {
      // Determine the hover target: the button parent (if it exists) or the link itself
      const accordionButton = link.closest('.megamenu-submenu__accordion-trigger');
      const hoverTarget = accordionButton || link;

      hoverTarget.addEventListener('mouseenter', () => {
        const megamenu = link.closest('.header-megamenu');
        
        if (megamenu) {
          megamenu.querySelectorAll('.megamenu-submenu__link.is-active').forEach(activeLink => {
            activeLink.classList.remove('is-active');
          });
        }
        
        link.classList.add('is-active');
      });

      // Remove active state when leaving the entire third level item (including fourth level children)
      const parentItem = link.closest('.megamenu-submenu__item');
      if (parentItem) {
        parentItem.addEventListener('mouseleave', (e) => {
          if (!parentItem.contains(e.relatedTarget)) {
            link.classList.remove('is-active');
          }
        });
      }
    });
  }

  setupFourthLevel() {
    const accordionTriggers = this.querySelectorAll('[data-accordion-trigger]');
    
    accordionTriggers.forEach(trigger => {
      const accordionContent = trigger.nextElementSibling;
      const accordionItem = trigger.closest('.megamenu-submenu__item');
      
      const openAccordion = () => {
        const parentSubmenu = trigger.closest('.megamenu-submenu__list');
        if (parentSubmenu) {
          const allTriggers = parentSubmenu.querySelectorAll('[data-accordion-trigger]');
          allTriggers.forEach(otherTrigger => {
            if (otherTrigger !== trigger) {
              otherTrigger.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        trigger.setAttribute('aria-expanded', 'true');
      };
      
      const closeAccordion = () => {
        trigger.setAttribute('aria-expanded', 'false');
      };
      
      trigger.addEventListener('mouseenter', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openAccordion();
      });
      
      if (accordionContent) {
        accordionContent.addEventListener('mouseenter', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        
        accordionContent.addEventListener('mouseleave', (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeAccordion();
        });
      }
      
      if (accordionItem) {
        accordionItem.addEventListener('mouseleave', (e) => {
          if (!accordionItem.contains(e.relatedTarget)) {
            closeAccordion();
          }
        });
      }
    });
  }

  closeAllAccordions(menuItem) {
    const accordions = menuItem.querySelectorAll('[data-accordion-trigger]');
    accordions.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }
  
  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openMenus = this.querySelectorAll('.list-menu__item');
        openMenus.forEach(item => {
          const link = item.querySelector('.header-nav__link');
          if (link) {
            link.setAttribute('aria-expanded', 'false');
          }
        });
        
        this.querySelectorAll('.megamenu-nav__item.is-active').forEach(item => {
          item.classList.remove('is-active');
        });

        openMenus.forEach(item => {
          this.closeAllAccordions(item);
        });
      }
    });
  }
}

if (!customElements.get('desktop-megamenu')) {
  customElements.define('desktop-megamenu', DesktopMegaMenu);
}


/**
 * Mobile Menu Drawer Web Component
 * Handles drawer open/close and sliding panel navigation
 */
class MobileMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.panelStack = [];
    this.mainList = null;
    this.overlayEl = null;
  }

  connectedCallback() {
    this.mainList = this.querySelector('.mobile-nav__list');
    
    // IMPORTANT: Remove is-active from ALL panels on init
    this.querySelectorAll('.mobile-nav__panel').forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });
    
    this.setupDrawer();
    this.setupPanels();
    this.setupResizeListener();
    this.ensureOverlayElement();
  }

  disconnectedCallback() {
    // Clean up event listeners
    const closeBtn = this.querySelector('[data-drawer-close]');
    const overlay = this.querySelector('[data-drawer-overlay]');
    const menuToggle = document.querySelector('[data-menu-toggle]');
    
    if (closeBtn) closeBtn.removeEventListener('click', this.closeDrawer);
    if (overlay) overlay.removeEventListener('click', this.closeDrawer);
    if (menuToggle) menuToggle.removeEventListener('click', this.openDrawer);
    
    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize);
  }

  setupDrawer() {
    const closeBtn = this.querySelector('[data-drawer-close]');
    const overlay = this.querySelector('[data-drawer-overlay]');
    const menuToggle = document.querySelector('[data-menu-toggle]');

    // Open drawer
    if (menuToggle) {
      menuToggle.addEventListener('click', () => this.openDrawer());
    }

    // Close drawer
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeDrawer());
    }

    if (overlay) {
      overlay.addEventListener('click', () => this.closeDrawer());
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        // Check if any layer is open
        const activeLayer = this.querySelector('.mobile-menu-drawer__layer.is-active');
        if (activeLayer) {
          this.closeLayer();
        } else if (this.panelStack.length > 0) {
          this.goBack();
        } else {
          this.closeDrawer();
        }
      }
    });
  }

  ensureOverlayElement() {
    const existing = document.querySelector('.optimized-predictive-search__overlay');
    if (existing) {
      this.overlayEl = existing;
      // Ensure clicking overlay also closes the drawer
      this.overlayEl.addEventListener('click', () => this.closeDrawer());
      return;
    }
    const overlay = document.createElement('div');
    overlay.className = 'optimized-predictive-search__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.addEventListener('click', () => this.closeDrawer());
    document.body.appendChild(overlay);
    this.overlayEl = overlay;
  }

  showOverlay() {
    if (!this.overlayEl) return;
    const body = document.body;
    const current = parseInt(body.dataset.overlayVisibleCount || '0', 10);
    body.dataset.overlayVisibleCount = String(current + 1);
    this.overlayEl.classList.add('is-visible');
  }

  hideOverlay() {
    if (!this.overlayEl) return;
    const body = document.body;
    const current = parseInt(body.dataset.overlayVisibleCount || '0', 10);
    const next = Math.max(0, current - 1);
    body.dataset.overlayVisibleCount = String(next);
    if (next === 0) {
      this.overlayEl.classList.remove('is-visible');
    }
  }

  lockBodyScroll() {
    const body = document.body;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;
    const current = parseInt(body.dataset.scrollLockCount || '0', 10);
    body.dataset.scrollLockCount = String(current + 1);
    body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    const body = document.body;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;
    const current = parseInt(body.dataset.scrollLockCount || '0', 10);
    const next = Math.max(0, current - 1);
    body.dataset.scrollLockCount = String(next);
    if (next === 0) {
      body.style.overflow = '';
      if (this.overlayEl) {
        this.overlayEl.classList.remove('is-visible');
      }
    }
  }

  setupPanels() {
    // Panel trigger buttons
    const triggers = this.querySelectorAll('[data-panel-trigger]');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const panelId = trigger.getAttribute('data-panel-trigger');
        this.openPanel(panelId);
      });
    });

    // Back buttons
    const backButtons = this.querySelectorAll('[data-panel-back]');
    
    backButtons.forEach(button => {
      button.addEventListener('click', () => this.goBack());
    });
    
    // Layer toggle buttons
    this.setupLayers();
  }
  
  setupLayers() {
    // Search layer toggle
    const searchToggle = this.querySelector('[data-search-toggle]');
    if (searchToggle) {
      searchToggle.addEventListener('click', () => this.openLayer('search'));
    }
    
    // Account layer toggle
    const accountToggle = this.querySelector('[data-account-toggle]');
    if (accountToggle) {
      accountToggle.addEventListener('click', () => this.openLayer('account'));
    }
    
    // Hamburger menu toggle - show main navigation
    const menuToggle = this.querySelector('[data-menu-toggle]');
    if (menuToggle) {
      menuToggle.addEventListener('click', () => this.showMainNav());
    }
    
    // Language dropdown toggle
    const languageButton = this.querySelector('.mobile-menu-drawer__footer .header-localization-button');
    if (languageButton) {
      languageButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleLanguageDropdown();
      });
    }
  }
  
  openLayer(layerName) {
    const layer = this.querySelector(`[data-layer="${layerName}"]`);
    if (!layer) return;
    
    // Hide all layers first
    this.querySelectorAll('.mobile-menu-drawer__layer').forEach(layer => {
      layer.classList.remove('is-active');
      layer.setAttribute('aria-hidden', 'true');
    });
    
    // Remove active state from all actions
    this.querySelectorAll('.mobile-menu-drawer__action').forEach(action => {
      action.classList.remove('is-active');
    });
    
    // Add active state to the corresponding action
    const actionButton = this.querySelector(`[data-${layerName}-toggle]`);
    if (actionButton) {
      actionButton.classList.add('is-active');
    }
    
    // Hide main navigation content but keep header visible
    const nav = this.querySelector('.mobile-menu-drawer__nav');
    if (nav) {
      nav.style.display = 'none';
    }
    
    // Hide footer when search or account layers are open
    const footer = this.querySelector('[data-footer]');
    if (footer && (layerName === 'search' || layerName === 'account')) {
      footer.style.display = 'none';
    }
    
    // Show the new layer
    layer.classList.add('is-active');
    layer.setAttribute('aria-hidden', 'false');
  }
  
  closeLayer() {
    // Remove active state from all actions
    this.querySelectorAll('.mobile-menu-drawer__action').forEach(action => {
      action.classList.remove('is-active');
    });
    
    // Hide all layers
    const layers = this.querySelectorAll('.mobile-menu-drawer__layer');
    layers.forEach(layer => {
      layer.classList.remove('is-active');
      layer.setAttribute('aria-hidden', 'true');
    });
    
    // Show main navigation
    const nav = this.querySelector('.mobile-menu-drawer__nav');
    if (nav) {
      nav.style.display = 'block';
    }
    
    // Show footer again when layers are closed
    const footer = this.querySelector('[data-footer]');
    if (footer) {
      footer.style.display = 'block';
    }
  }
  
  toggleLanguageDropdown() {
    const languageMenu = this.querySelector('.mobile-menu-drawer__footer .header-localization-menu');
    const languageButton = this.querySelector('.mobile-menu-drawer__footer .header-localization-button');
    
    if (!languageMenu || !languageButton) return;
    
    const isExpanded = languageButton.getAttribute('aria-expanded') === 'true';
    
    languageButton.classList.toggle('is-active');
    languageMenu.classList.toggle('show');
    languageButton.setAttribute('aria-expanded', !isExpanded);
    
    // Scroll to show the expanded content (accordion behavior)
    if (!isExpanded) {
      // Give the menu time to render and expand, then scroll
      setTimeout(() => {
        const contentArea = this.querySelector('.mobile-menu-drawer__content-area');
        const languageMenuWrapper = this.querySelector('.mobile-menu-drawer__footer');
        
        if (contentArea && languageMenuWrapper) {
          // Get current scroll position
          const currentScroll = contentArea.scrollTop;
          
          // Get the height of the expanded menu
          const menuHeight = languageMenu.scrollHeight;
          
          // Scroll down by the menu height to reveal the expanded content
          contentArea.scrollTo({
            top: currentScroll + menuHeight,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to let the CSS transition start
    }
  }
  
  showMainNav() {
    // Remove active state from all actions
    this.querySelectorAll('.mobile-menu-drawer__action').forEach(action => {
      action.classList.remove('is-active');
    });
    
    // Add active state to hamburger button
    const menuToggle = this.querySelector('[data-menu-toggle]');
    if (menuToggle) {
      menuToggle.classList.add('is-active');
    }
    
    // Hide all layers
    const layers = this.querySelectorAll('.mobile-menu-drawer__layer');
    layers.forEach(layer => {
      layer.classList.remove('is-active');
      layer.setAttribute('aria-hidden', 'true');
    });
    
    // Show main navigation
    const nav = this.querySelector('.mobile-menu-drawer__nav');
    if (nav) {
      nav.style.display = 'block';
    }
    
    // Show footer when returning to main navigation
    const footer = this.querySelector('[data-footer]');
    if (footer) {
      footer.style.display = 'block';
    }
  }
  

  openDrawer() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.lockBodyScroll();
    // Hide internal overlay to avoid double-dimming; use global overlay instead
    const internalOverlay = this.querySelector('.mobile-menu-drawer__overlay');
    if (internalOverlay) internalOverlay.style.display = 'none';
    this.showOverlay();
    
    // Calculate and set footer height for proper scrolling
    this.updateFooterHeight();
    
    // Don't reset to main nav - keep the last state
    // Only show main nav if nothing is active
    const hasActivePanel = this.panelStack && this.panelStack.length > 0;
    const hasActiveLayer = this.querySelector('.mobile-menu-drawer__layer.is-active');
    
    if (!hasActivePanel && !hasActiveLayer) {
      this.showMainNav();
    }
    
    // Update footer visibility based on current state
    this.updateFooterVisibility();
  }

  closeDrawer() {
    this.classList.add('closing');
    this.setAttribute('aria-hidden', 'true');
    // Restore internal overlay display
    const internalOverlay = this.querySelector('.mobile-menu-drawer__overlay');
    if (internalOverlay) internalOverlay.style.display = '';
    
    // Don't close layers or reset panels - keep the state
    // Just hide the drawer
    
    // Remove both classes after transition completes
    setTimeout(() => {
      this.classList.remove('is-open', 'closing');
      // Don't reset panels - keep them for next time
      this.unlockBodyScroll();
      this.hideOverlay();
    }, 300);
  }

  updateFooterHeight() {
    const footer = this.querySelector('.mobile-menu-drawer__footer');
    const spacer = this.querySelector('.mobile-menu-drawer__spacer');
    const contentArea = this.querySelector('.mobile-menu-drawer__content-area');
    
    if (footer && contentArea) {
      // Calculate footer height + spacer height
      const footerHeight = footer.offsetHeight + (spacer ? spacer.offsetHeight : 0);
      
      // Update CSS variable
      document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);
      
      // Add class for browsers that don't support :has()
      contentArea.classList.add('has-footer');
    } else if (contentArea) {
      // Reset if no footer
      document.documentElement.style.setProperty('--footer-height', '0px');
      contentArea.classList.remove('has-footer');
    }
  }

  setupResizeListener() {
    this.handleResize = () => {
      if (this.classList.contains('is-open')) {
        this.updateFooterHeight();
      }
    };
    
    window.addEventListener('resize', this.handleResize);
  }

  openPanel(panelId) {
    const panel = document.getElementById(panelId);
    
    if (!panel) {
      console.error('Panel not found:', panelId);
      return;
    }

    // Only transition the top-most active panel (if any) and the target panel
    const previousPanel = this.panelStack.length > 0 ? this.panelStack[this.panelStack.length - 1] : null;

    // Temporarily disable transitions on all panels EXCEPT the target panel
    // This ensures only the target slides in; previous hides instantly
    const allPanels = this.querySelectorAll('.mobile-nav__panel');
    allPanels.forEach(p => {
      if (p !== panel) {
        p.classList.add('no-transition');
      }
    });

    // Hide only the previous panel (if any)
    if (previousPanel) {
      previousPanel.classList.remove('is-active');
      previousPanel.setAttribute('aria-hidden', 'true');
    }

    // Show the new target panel
    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    
    // Add to stack
    this.panelStack.push(panel);
    
    // Reset scroll position for iOS/Safari: ensure new panel appears from top
    const contentArea = this.querySelector('.mobile-menu-drawer__content-area');
    // Use double rAF to reliably flush layout and then set scroll, especially on iOS
    requestAnimationFrame(() => {
      if (contentArea) contentArea.scrollTop = 0;
      panel.scrollTop = 0;
    });

    // Re-enable transitions on the next frame
    requestAnimationFrame(() => {
      allPanels.forEach(p => p.classList.remove('no-transition'));
      // Update footer visibility based on panel level
      this.updateFooterVisibility();
    });
  }

  goBack() {
    if (this.panelStack.length === 0) return;

    // Get current panel and remove it from stack
    const currentPanel = this.panelStack.pop();

    const previousPanel = this.panelStack.length > 0 ? this.panelStack[this.panelStack.length - 1] : null;

    // Temporarily disable transitions on all panels EXCEPT the currentPanel
    // This ensures only the current slides out; previous appears instantly
    const allPanels = this.querySelectorAll('.mobile-nav__panel');
    allPanels.forEach(p => {
      if (p !== currentPanel) {
        p.classList.add('no-transition');
      }
    });

    // Slide current panel out to the right
    currentPanel.classList.remove('is-active');
    currentPanel.setAttribute('aria-hidden', 'true');

    // Show previous panel if there is one (slide it back in)
    if (previousPanel) {
      previousPanel.classList.add('is-active');
      previousPanel.setAttribute('aria-hidden', 'false');
    }
    // If stack is empty, first level is automatically visible

    // Re-enable transitions on the next frame and update footer
    requestAnimationFrame(() => {
      allPanels.forEach(p => p.classList.remove('no-transition'));
      // Reset scroll positions to top when navigating back as well
      const contentArea = this.querySelector('.mobile-menu-drawer__content-area');
      if (contentArea) contentArea.scrollTop = 0;
      if (previousPanel) previousPanel.scrollTop = 0;
      this.updateFooterVisibility();
    });
  }

  resetPanels() {
    // Close all panels
    this.panelStack.forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });
    this.panelStack = [];
    // First level is automatically visible (nested structure)
    
    // Show footer when back to first level
    this.updateFooterVisibility();
  }
  
  updateFooterVisibility() {
    const footer = this.querySelector('[data-footer]');
    if (!footer) return;
    
    // If a top-level layer (e.g., search/account) is active, always hide footer
    const activeLayer = this.querySelector('.mobile-menu-drawer__layer.is-active');
    if (activeLayer) {
      footer.style.display = 'none';
      this.classList.add('footer-hidden');
      return;
    }

    // Check if we're on level 2, 3, or 4
    const currentLevel = this.panelStack.length > 0 
      ? parseInt(this.panelStack[this.panelStack.length - 1].getAttribute('data-panel-level'))
      : 1;
    
    // Hide footer on levels 2, 3, and 4; show on level 1
    if (currentLevel >= 2) {
      footer.style.display = 'none';
      this.classList.add('footer-hidden');
    } else {
      footer.style.display = '';
      this.classList.remove('footer-hidden');
    }
  }

}

if (!customElements.get('mobile-menu-drawer')) {
  customElements.define('mobile-menu-drawer', MobileMenuDrawer);
}

/**
 * Header Scroll Detection
 * Note: Removed scroll handler as secondary nav is no longer sticky
 */
